import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import logo from "@/assets/logo.gif";

/**
 * The frame the signed-out screens share: sign-in, asking for a reset link,
 * and setting the new password.
 *
 * Not `Page` and not `Card`: those are drawn inside the shell, under its
 * header, and none of that exists before there is a session. This is the one
 * place the app is a single column in the middle of an empty viewport.
 */
export function AuthScreen({
  title,
  children,
  footer,
}: {
  title: ReactNode;
  children: ReactNode;
  /** The strip that closes the panel: the way to the neighbouring screen. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <main className="grid min-h-dvh place-items-center bg-ground px-4 py-10 text-ink">
      <div className="w-full max-w-[420px]">
        <img src={logo} alt={t("app.name")} className="mb-8 h-[38px] w-auto" />
        <section className="border-2 border-rule">
          <header className="border-b-2 border-rule px-5 py-4">
            <p className="kicker mb-1.5 text-brand-700">{t("app.name")}</p>
            <h1 className="mb-0 text-[30px]">{title}</h1>
          </header>
          <div className="px-5 py-5">{children}</div>
          {footer ? (
            <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t-2 border-rule bg-panel px-5 py-3 text-sm">
              {footer}
            </footer>
          ) : null}
        </section>
      </div>
    </main>
  );
}

/** Something went right, or is merely worth knowing. Not an error. */
export function AuthNotice({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      className="mb-4 border-2 border-rule bg-panel px-3 py-2 text-sm last:mb-0"
    >
      {children}
    </p>
  );
}

/** The same box `NuevoSeguimiento` refuses a form with. */
export function AuthError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
      {children}
    </p>
  );
}
